import { ReactNode, useMemo, useState, useCallback, useEffect } from "react";
import { useTheme, useMediaQuery, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import { cloneDeep, isEqual } from "lodash-es";
import { Redirect, Route, RouteProps, Switch, useRouteMatch } from "react-router-dom";
import { Page } from "@mb-pro-ui/components";
import { SideBarOption } from "@mb-pro-ui/components/SideBar";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import { Accountinfo as AccountinfoValue, ExtraTemplateItem } from "../../utils/types";
import ConfigInfo from "../ConfigInfo";
import TemplateInfo from "../TemplateInfo";
import Accountinfo from "./Accountinfo";
import ConfigSample from "./ConfigSample";
import { useDevice } from "../../utils/DeviceProvider";
import BasicTemplateHeader from "./BasicTemplateHeader";
import ConfigHistory from "../ConfigHistory";
import useEditedStyles from "../../hooks/useEditedStyles";
import { useMenuButtonContainer } from "../header/MenuButtonContainerProvider";
import JsonEditor, { isEdited } from "./extraTemplate/JsonEditor";

import { useBeforeSave as useSmartalarmBeforeSave, useSmartalarmSubPages, loadConfig as smartalarmLoadConfig } from "./basic/smartalarm/SmartalarmTemplate";
import {
  useBeforeSave as useThermowatchBeforeSave,
  useThermowatchSubPages,
  loadConfig as thermowatchLoadConfig,
  useRuleTypes,
} from "./basic/thermowatch/ThermowatchTemplate";
import DynamicIcon from "./basic/DynamicIcon";
import DeviceSettings, { loadConfig as deviceSettingsLoadConfig } from "./DeviceSettings";
import TemplateBlocks from "./extraTemplate/TemplateBlocks";
import { useServerCommunication } from "../../utils/ServerCommunicaton";
import { ModularDeviceTemplate, TemplateHelperInfoBlocks } from "@helios/toolbox-types";
import { useMutation } from "react-query";
import { TemplateHelperInfoProvider } from "../../utils/TemplateHelperInfoProvider";
import { usePageSxProps } from "../../pages/styles";
import { SidebarOpenProvider } from "../SidebarOpenProvider";
import { ExportImport } from "../ExportImport";
import { FirmwareUpdateSubpage } from "../FirmwareUpdateSubpage";
import { EmailNotificationSubpage } from "../EmailNotificationSubpage";
import { useUserInfo } from "../../utils/user-info";
import { ToolboxLabel } from "../ToolboxLabel";
import { useErrorHandler } from "../../hooks/useErrorHandler";

export const reservedTemplateKeys = ["info", "status"];

export interface TemplateSubPage {
  sidebarOption: SideBarOption;
  children?: ReactNode | Array<{ link: string; children: ReactNode }>;
  render?: () => ReactNode;
}

export type BeforeSaveErrorType = { title: string; details?: string[] };
export type BeforeSaveFunction<T> = (value: T) => { processed: T; errors?: BeforeSaveErrorType[] };
export interface BasicTemplateComponentProps {
  templateParts: string[];
  subPages: TemplateSubPage[];
}
export interface BasicTemplateComponentValue {
  accountinfo: AccountinfoValue | null;
  [key: string]: any;
}

interface TemplateRouteProps extends RouteProps {
  path: string;
  basePath: string;
}

const templateRoute = ({ path, basePath, ...rest }: TemplateRouteProps) => {
  return <Route key={`page-${basePath}${path}`} path={`${basePath}${path}`} {...rest} />;
};

const customTemplateRoute = (basePath: string, subPages: { sidebarOption: SideBarOption; children: Array<{ link: string; children: ReactNode }> }) => {
  const { children } = subPages;
  return children.map(({ link: subLink, children }) =>
    templateRoute({
      basePath,
      path: subLink,
      children,
    })
  );
};

const BasicTemplate = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const {
    device: { config, currentValue, template, unitid },
    setCurrentValue,
  } = useDevice();
  const [user] = useUserInfo();

  const { path: basePath } = useRouteMatch<{ unitid: string }>();

  const theme = useTheme();
  const editedStyle = useEditedStyles();
  const menuStyles = useMenuStyles();
  const { singlePageSx } = usePageSxProps();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { containerRef } = useMenuButtonContainer();
  const { formatMessage } = useIntl();

  const { getTemplateHelperInfo } = useServerCommunication();

  const { errorHandler: onError } = useErrorHandler([]);

  const templateHelperInfoQuery = useCallback(
    () => (template ? getTemplateHelperInfo(template.id, unitid) : Promise.resolve({} as TemplateHelperInfoBlocks)),
    [template, getTemplateHelperInfo, unitid]
  );
  const { mutate: getTemplateHelperBlocks, data: helperBlocks } = useMutation({ mutationFn: templateHelperInfoQuery, onError });

  const basicInfoEdited = useBasicInfoEdited();

  const smartalarmBeforeSave = useSmartalarmBeforeSave();
  const smartalarmSubPages = useSmartalarmSubPages();
  const thermowatchBeforeSave = useThermowatchBeforeSave();
  const thermowatchSubPages = useThermowatchSubPages();
  const thermowatchRuleTypes = useRuleTypes();

  const openSidebar = useCallback(() => setSidebarOpen(true), [setSidebarOpen]);
  const closeSidebar = useCallback(() => setSidebarOpen(false), [setSidebarOpen]);

  const beforeSave = useCallback<BeforeSaveFunction<any>>(
    (currentValue: any) => {
      let res = { processed: cloneDeep(currentValue) } as { processed: any; errors?: BeforeSaveErrorType[] };
      if (template?.template && Object.keys(template.template).includes("smartalarm") && res.processed.smartalarm) {
        const { processed, errors } = smartalarmBeforeSave(res.processed.smartalarm);
        res.processed.smartalarm = processed;
        if (errors) {
          res.errors = res.errors ? [...res.errors, ...errors] : errors;
        }
      }
      if (template?.template && Object.keys(template.template).includes("thermowatch") && res.processed.thermowatch) {
        const { processed, errors } = thermowatchBeforeSave(res.processed.thermowatch);
        res.processed.thermowatch = processed;
        if (errors) {
          res.errors = res.errors ? [...res.errors, ...errors] : errors;
        }
      }
      return res;
    },
    [template, smartalarmBeforeSave, thermowatchBeforeSave]
  );

  const loadConfig = useCallback(
    (configSource: any) => {
      if (!template) {
        return;
      }
      const { accountinfo } = configSource;
      const { name, description, account } = accountinfo || {};
      const newConfig = { accountinfo: { name, description } } as any;
      if (account !== undefined) {
        newConfig.accountinfo.account = account;
      }
      Object.keys(template.template)
        .filter((key) => !reservedTemplateKeys.includes(key))
        .forEach((key) => {
          switch (key) {
            case "devicesettings":
              newConfig.devicesettings = deviceSettingsLoadConfig(configSource.devicesettings);
              break;
            case "smartalarm":
              newConfig.smartalarm = smartalarmLoadConfig(configSource.smartalarm, template.template);
              break;
            case "thermowatch":
              newConfig.thermowatch = thermowatchLoadConfig(configSource.thermowatch, template.template, thermowatchRuleTypes);
              break;
            default:
              newConfig[key] = cloneDeep(configSource[key]);
          }
        });
      newConfig.templateId = template.id;
      newConfig.configVersion = config?.toolboxConfig?.configVersion ?? 0;
      setCurrentValue(newConfig);
    },
    [config, setCurrentValue, template, thermowatchRuleTypes]
  );

  useEffect(() => {
    if (!template) {
      return;
    }
    getTemplateHelperBlocks();
  }, [getTemplateHelperBlocks, template]);

  const infoSubPage = useMemo(() => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={sidebarOpen ? 6 : 4} lg={sidebarOpen ? 4 : 3} xl={3} sx={{ display: "flex" }}>
          <TemplateInfo />
        </Grid>
        <Grid item xs={12} sm={6} md={sidebarOpen ? 6 : 4} lg={sidebarOpen ? 4 : 3} xl={3} sx={{ display: "flex" }}>
          <ConfigInfo />
        </Grid>
        <Grid item xs={12} sm={6} md={sidebarOpen ? 6 : 4} lg={sidebarOpen ? 4 : 3} xl={3} sx={{ display: "flex" }}>
          <Accountinfo />
        </Grid>
        {!template?.template?.info?.parsed && false ? (
          <Grid item xs={12} sm={6} md={sidebarOpen ? 6 : 4} lg={sidebarOpen ? 4 : 3} xl={3} sx={{ display: "flex" }}>
            <DeviceSettings />
          </Grid>
        ) : null}
      </Grid>
    );
  }, [sidebarOpen, template]);
  const configSample = useMemo(() => <ConfigSample beforeSave={beforeSave} loadConfig={loadConfig} />, [beforeSave, loadConfig]);
  const history = useMemo(() => <ConfigHistory loadConfig={loadConfig} />, [loadConfig]);
  const exportImport = useMemo(() => <ExportImport loadConfig={loadConfig} />, [loadConfig]);
  const firmware = useMemo(() => <FirmwareUpdateSubpage />, []);
  const email = useMemo(() => <EmailNotificationSubpage />, []);

  const sidebarOptions = useMemo(() => {
    const subPages = template?.template
      ? Object.entries(template.template)
          .filter(([key]) => !reservedTemplateKeys.includes(key) && key !== "devicesettings")
          .map(([key, value]) => {
            switch (key) {
              case "smartalarm": {
                const { sidebarOption } = smartalarmSubPages;
                return sidebarOption;
              }
              case "thermowatch": {
                const { sidebarOption } = thermowatchSubPages;
                return sidebarOption;
              }
              default:
                return {
                  link: `/${key}`,
                  name: (value as ExtraTemplateItem).convertToOld
                    ? formatMessage({ defaultMessage: "Configuration" })
                    : (value as ExtraTemplateItem).title || key,
                  icon: (value as ExtraTemplateItem).icon ? <DynamicIcon iconName={(value as ExtraTemplateItem).icon as string} /> : undefined,
                  classname: isEdited(currentValue, config, key) ? editedStyle.menuItem : "",
                };
            }
          })
      : ([] as SideBarOption[]);
    return [
      {
        link: "",
        name: <FormattedMessage defaultMessage="Basic information" />,
        icon: <InfoOutlinedIcon />,
        classname: basicInfoEdited ? editedStyle.menuItem : "",
      },
      ...subPages,
      !user?.rights?.limited_user ? {
        link: "/config-history",
        name: <FormattedMessage defaultMessage="Config history" />,
        icon: <HistoryIcon />,
        classname: menuStyles.withSeparator,
      } : null,
      !user?.rights?.limited_user ? {
        link: "/export-import",
        name: <FormattedMessage defaultMessage="Export / import" />,
        icon: <ImportExportIcon />,
      } : null,
      !user?.rights?.limited_user ? {
        link: "/config",
        name: <FormattedMessage defaultMessage="Sample config" />,
        icon: <BugReportOutlinedIcon />,
      } : null,
      !user?.rights?.limited_user ? {
        link: `/email`,
        name: <FormattedMessage defaultMessage="E-mail notification" />,
        icon: <EmailOutlinedIcon />,
        classname: menuStyles.withSeparator,
      }: null,
      user?.rights?.firmware_update && !user?.rights?.limited_user
        ? {
            link: `/firmware-update`,
            name: <FormattedMessage defaultMessage="Firmware update" />,
            icon: <SystemUpdateIcon />,
          }
        : null,
    ]
      .filter((item) => item !== null)
      .map((item) => item as SideBarOption);
  }, [template, basicInfoEdited, editedStyle, menuStyles, user, formatMessage, currentValue, config, smartalarmSubPages, thermowatchSubPages]);

  const deviceMenuHeader = useMemo(() => <ToolboxLabel />, []);

  // useEffect(() => {
  //   fetch("http://localhost:3001", { method: "GET" })
  //     .then((res) => res.text())
  //     .then(() => console.log("localhost elérhető"))
  //     .catch(() => console.log("localhost nem elérhető"));
  //   fetch("http://192.168.111.78:3001/", { method: "GET" })
  //     .then((res) => res.text())
  //     .then(() => console.log("http://192.168.111.78:3001/ elérhető"))
  //     .catch(() => console.log("http://192.168.111.78:3001/ nem elérhető"));
  // }, []);

  // useEffect(() => {
  //   if ((!config && !noConfig) || (currentValue && currentValue.accountinfo) || (!noConfig && !currentValue)) {
  //     return;
  //   }
  //   const value = { accountinfo: null, ...currentValue };
  //   setEmptyConfig(value);
  //   setCurrentValue(value);
  // }, [currentValue, noConfig, config, setCurrentValue, setEmptyConfig]);

  return (
    <SidebarOpenProvider open={sidebarOpen}>
      <Page
        sidebarHeader={deviceMenuHeader}
        sidebarVersion="v1"
        sidebarOptions={sidebarOptions}
        onSidebarOpen={openSidebar}
        onSidebarClose={closeSidebar}
        sidebarVariant={smallScreen ? "temporary" : undefined}
        sidebarDefaultOpen={!smallScreen}
        sidebarMenuButtonContainer={containerRef.current || undefined}
        sx={{
          ...singlePageSx,
          paddingBottom: 2,
          paddingTop: 0,
        }}
      >
        <BasicTemplateHeader beforeSave={beforeSave} />
        <Switch>
          <Route exact path={basePath}>
            {infoSubPage}
          </Route>
          {template?.template &&
            Object.entries(template.template)
              .filter(([key]) => !reservedTemplateKeys.includes(key) && key !== "devicesettings")
              .map(([key, value]) => {
                switch (key) {
                  case "smartalarm":
                    return customTemplateRoute(basePath, smartalarmSubPages);
                  case "thermowatch":
                    return customTemplateRoute(basePath, thermowatchSubPages);
                  default:
                    return templateRoute({
                      basePath,
                      path: `/${key}`,
                      children: (value as ExtraTemplateItem).blocks ? (
                        <TemplateHelperInfoProvider templateHelperInfo={helperBlocks?.[key] ?? {}} refetchHelperInfo={getTemplateHelperBlocks}>
                          <TemplateBlocks configKey={key} blocks={(value as ModularDeviceTemplate).blocks} endUserKeys={(value as ModularDeviceTemplate).endUserKeys} />
                        </TemplateHelperInfoProvider>
                      ) : (
                        <JsonEditor configKey={key} title={(value as ExtraTemplateItem).title || key} />
                      ),
                    });
                }
              })}
          {templateRoute({
            basePath,
            path: "/config",
            children: configSample,
          })}
          {templateRoute({
            basePath,
            path: "/config-history",
            children: history,
          })}
          {templateRoute({
            basePath,
            path: "/export-import",
            children: exportImport,
          })}
          {templateRoute({
            basePath,
            path: "/firmware-update",
            children: firmware,
          })}
          {templateRoute({
            basePath,
            path: "/email",
            children: email,
          })}
          <Redirect from={basePath} to={basePath} />
        </Switch>
      </Page>
    </SidebarOpenProvider>
  );
};

const useMenuStyles = makeStyles(() => {
  const theme = useTheme();

  return {
    withSeparator: {
      "& .MuiListItem-root": {
        borderTopWidth: 1,
        borderTopColor: theme.palette.divider,
        borderTopStyle: "solid",
      },
    },
  };
});

const useBasicInfoEdited = () => {
  const {
    device: { currentValue, config },
  } = useDevice();

  return (
    !isEqual(currentValue?.accountinfo, config?.toolboxConfig?.accountinfo) || !isEqual(currentValue?.devicesettings, config?.toolboxConfig?.devicesettings)
  );
};

export default BasicTemplate;
